import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  FileText, Download, Shield, Lock, FileCheck, 
  BookOpen, CheckSquare, BarChart3, AlertTriangle
} from 'lucide-react';

const DownloadsPage = () => {
  const downloads = [
    {
      id: 1,
      title: 'Guia Completo ISO 27001:2022',
      description: 'Tudo que você precisa saber sobre a nova versão da norma internacional de segurança da informação.',
      category: 'E-book',
      pages: '45 páginas',
      format: 'PDF',
      icon: BookOpen,
      color: 'from-blue-500 to-cyan-500',
      featured: true
    },
    {
      id: 2,
      title: 'Checklist de Adequação LGPD',
      description: 'Lista completa de verificação para garantir conformidade com a Lei Geral de Proteção de Dados.',
      category: 'Checklist',
      pages: '12 páginas',
      format: 'PDF',
      icon: CheckSquare,
      color: 'from-emerald-500 to-teal-500'
    },
    {
      id: 3,
      title: 'Template de Política de Segurança',
      description: 'Modelo editável de política de segurança da informação baseado nas melhores práticas.',
      category: 'Template',
      pages: '28 páginas',
      format: 'DOCX',
      icon: FileText,
      color: 'from-violet-500 to-purple-500'
    },
    {
      id: 4,
      title: 'Matriz de Riscos - Planilha',
      description: 'Ferramenta pronta para uso na identificação e avaliação de riscos de segurança.',
      category: 'Ferramenta',
      pages: 'Planilha',
      format: 'XLSX',
      icon: BarChart3,
      color: 'from-orange-500 to-amber-500'
    },
    {
      id: 5,
      title: 'Guia de Resposta a Incidentes',
      description: 'Procedimentos e fluxos para resposta eficiente a incidentes de segurança.',
      category: 'Guia',
      pages: '32 páginas',
      format: 'PDF',
      icon: AlertTriangle,
      color: 'from-red-500 to-pink-500'
    },
    {
      id: 6,
      title: 'PPSI 2.0 - Resumo Executivo',
      description: 'Visão geral dos 25 controles do Programa de Privacidade e Segurança da Informação.',
      category: 'Resumo',
      pages: '18 páginas',
      format: 'PDF',
      icon: Shield,
      color: 'from-cyan-500 to-blue-500'
    },
    {
      id: 7,
      title: 'Glossário de Cybersegurança',
      description: 'Termos e definições essenciais para profissionais de segurança da informação.',
      category: 'Referência',
      pages: '24 páginas',
      format: 'PDF',
      icon: BookOpen,
      color: 'from-indigo-500 to-violet-500'
    },
    {
      id: 8,
      title: 'Checklist de Auditoria ISO 27001',
      description: 'Lista de verificação para preparação e execução de auditorias de certificação.',
      category: 'Checklist',
      pages: '35 páginas',
      format: 'PDF',
      icon: FileCheck,
      color: 'from-teal-500 to-emerald-500'
    }
  ];

  const handleDownload = (item) => {
    // In production, this would trigger actual download
    // For now, show alert that it's a demo
    alert(`Em breve! O download de "${item.title}" estará disponível.`);
  };

  const featuredItem = downloads.find(d => d.featured);
  const regularItems = downloads.filter(d => !d.featured);

  return (
    <>
      <Helmet>
        <title>Central de Downloads - DMM Security</title>
        <meta name="description" content="Baixe gratuitamente e-books, checklists, templates e ferramentas de segurança da informação e compliance." />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-24" data-testid="downloads-page">
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#1a3a52] to-[#10b981] text-white py-16">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl mx-auto text-center"
            >
              <span className="inline-block px-4 py-1.5 bg-white/10 rounded-full text-sm font-medium mb-4">
                Central de Downloads
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Materiais Gratuitos
              </h1>
              <p className="text-lg text-gray-200">
                E-books, checklists, templates e ferramentas para elevar a maturidade de segurança da sua organização
              </p>
            </motion.div>
          </div>
        </section>

        <div className="container mx-auto px-4 py-12">
          {/* Featured Download */}
          {featuredItem && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl overflow-hidden shadow-lg mb-12"
            >
              <div className="md:flex">
                <div className={`md:w-1/3 bg-gradient-to-br ${featuredItem.color} p-8 flex items-center justify-center`}>
                  <div className="text-center text-white">
                    <featuredItem.icon className="w-20 h-20 mx-auto mb-4 opacity-90" />
                    <span className="text-sm font-medium opacity-75">{featuredItem.category}</span>
                  </div>
                </div>
                <div className="md:w-2/3 p-8">
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="inline-block px-3 py-1 bg-[#10b981]/10 text-[#10b981] text-xs font-bold rounded-full mb-3">
                        DESTAQUE
                      </span>
                      <h2 className="text-2xl md:text-3xl font-bold text-[#1a3a52] mb-3">
                        {featuredItem.title}
                      </h2>
                      <p className="text-gray-600 mb-6 text-lg">
                        {featuredItem.description}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
                        <span className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          {featuredItem.pages}
                        </span>
                        <span className="px-2 py-0.5 bg-gray-100 rounded text-xs font-medium">
                          {featuredItem.format}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownload(featuredItem)}
                    className="inline-flex items-center gap-2 bg-[#10b981] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#0ea573] transition-all hover:shadow-lg hover:shadow-[#10b981]/25"
                  >
                    <Download className="w-5 h-5" />
                    Baixar Gratuitamente
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Downloads Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {regularItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all group"
              >
                <div className={`h-32 bg-gradient-to-br ${item.color} flex items-center justify-center`}>
                  <item.icon className="w-12 h-12 text-white opacity-90 group-hover:scale-110 transition-transform" />
                </div>
                <div className="p-5">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {item.category}
                  </span>
                  <h3 className="font-bold text-[#1a3a52] mt-1 mb-2 group-hover:text-[#10b981] transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {item.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{item.pages}</span>
                      <span className="px-1.5 py-0.5 bg-gray-100 rounded">
                        {item.format}
                      </span>
                    </div>
                    <button
                      onClick={() => handleDownload(item)}
                      className="p-2 rounded-lg bg-[#10b981]/10 text-[#10b981] hover:bg-[#10b981] hover:text-white transition-all"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 bg-gradient-to-br from-[#1a3a52] to-[#2a5a72] rounded-2xl p-8 md:p-12 text-white text-center"
          >
            <Lock className="w-12 h-12 mx-auto mb-4 text-[#10b981]" />
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Precisa de materiais personalizados?
            </h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Nossa equipe pode desenvolver políticas, procedimentos e materiais de treinamento sob medida para sua organização.
            </p>
            <a
              href="/contato"
              className="inline-flex items-center gap-2 bg-[#10b981] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#0ea573] transition-all"
            >
              Solicitar Orçamento
            </a>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default DownloadsPage;
